/* ==========================================================================
   OTM Product Detail Experience — behaviors (System 1)
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies. Everything is a custom element so sections
   stay independent and the Theme Editor can add/remove/reorder them freely.
   All components are progressive enhancements: the underlying Liquid works
   without JavaScript (native forms, native scrolling, <details> accordions).
   ========================================================================== */
(function () {
  'use strict';

  var RECENT_KEY = 'otm:recently-viewed';
  var RECENT_MAX = 12;

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  /* ------------------------------------------------------------------
     Gallery: thumbnail sync, scroll tracking, lightbox zoom
     ------------------------------------------------------------------ */
  var OtmGallery = (function () {
    function init(el) {
      var viewport = el.querySelector('[data-otm-gallery-viewport]');
      var slides = Array.prototype.slice.call(el.querySelectorAll('[data-otm-slide]'));
      var thumbs = Array.prototype.slice.call(el.querySelectorAll('[data-otm-thumb]'));
      var lightbox = el.querySelector('dialog[data-otm-lightbox]');
      var lightboxImg = lightbox ? lightbox.querySelector('[data-otm-lightbox-img]') : null;
      var current = 0;

      function setCurrent(index, scroll) {
        current = Math.max(0, Math.min(index, slides.length - 1));
        thumbs.forEach(function (t, i) {
          t.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
        if (scroll && slides[current]) {
          viewport.scrollTo({ left: slides[current].offsetLeft, behavior: 'smooth' });
        }
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { setCurrent(i, true); });
      });

      if ('IntersectionObserver' in window && viewport) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              setCurrent(slides.indexOf(entry.target), false);
            }
          });
        }, { root: viewport, threshold: 0.6 });
        slides.forEach(function (s) { observer.observe(s); });
      }

      function openLightbox(index) {
        if (!lightbox || !lightboxImg) return;
        var src = slides[index] && slides[index].getAttribute('data-otm-zoom-src');
        if (!src) return;
        lightboxImg.src = src;
        lightboxImg.alt = slides[index].getAttribute('data-otm-alt') || '';
        lightbox.showModal();
      }

      el.addEventListener('click', function (event) {
        var zoomBtn = event.target.closest('[data-otm-zoom]');
        if (zoomBtn) {
          openLightbox(slides.indexOf(zoomBtn.closest('[data-otm-slide]')));
        }
        if (event.target.closest('[data-otm-lightbox-close]')) lightbox.close();
        if (event.target.closest('[data-otm-lightbox-prev]')) stepLightbox(-1);
        if (event.target.closest('[data-otm-lightbox-next]')) stepLightbox(1);
      });

      function stepLightbox(delta) {
        var zoomable = slides.filter(function (s) { return s.hasAttribute('data-otm-zoom-src'); });
        var visible = slides[current];
        var idx = zoomable.indexOf(visible);
        if (idx === -1) idx = 0;
        var next = zoomable[(idx + delta + zoomable.length) % zoomable.length];
        setCurrent(slides.indexOf(next), true);
        if (lightboxImg) {
          lightboxImg.src = next.getAttribute('data-otm-zoom-src');
          lightboxImg.alt = next.getAttribute('data-otm-alt') || '';
        }
      }

      if (lightbox) {
        lightbox.addEventListener('click', function (event) {
          if (event.target === lightbox) lightbox.close();
        });
      }

      // Featured-media sync on variant change
      document.addEventListener('otm:variant-change', function (event) {
        var mediaId = event.detail && event.detail.featuredMediaId;
        if (!mediaId) return;
        var target = slides.findIndex(function (s) {
          return s.getAttribute('data-otm-media-id') === String(mediaId);
        });
        if (target > -1) setCurrent(target, true);
      });
    }
    return { init: init };
  })();

  /* ------------------------------------------------------------------
     Product form: variant select, price/availability updates, AJAX add
     ------------------------------------------------------------------ */
  var OtmProductForm = (function () {
    function readJSON(el) {
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    function init(root) {
      var dataEl = root.querySelector('script[data-otm-product-json]');
      var data = dataEl ? readJSON(dataEl) : null;
      if (!data) return;

      var select = root.querySelector('[data-otm-variant-select]');
      var form = document.getElementById(root.getAttribute('data-otm-form-id') || '');
      var feedback = root.querySelector('[data-otm-feedback]');
      var ajaxAdd = root.getAttribute('data-otm-ajax') === 'true';

      function currentVariant() {
        if (!select) return data.variants[0];
        var id = select.value;
        return data.variants.find(function (v) { return String(v.id) === String(id); }) || data.variants[0];
      }

      function render(variant) {
        if (!variant) return;
        document.querySelectorAll('[data-otm-price-current]').forEach(function (el) {
          el.textContent = variant.price_formatted;
        });
        document.querySelectorAll('[data-otm-price-compare]').forEach(function (el) {
          if (variant.compare_formatted) {
            el.textContent = variant.compare_formatted;
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
        document.querySelectorAll('[data-otm-availability]').forEach(function (el) {
          el.dataset.state = variant.available ? 'available' : 'unavailable';
          el.textContent = variant.available ? el.getAttribute('data-otm-label-in-stock') : el.getAttribute('data-otm-label-sold-out');
        });
        document.querySelectorAll('[data-otm-submit]').forEach(function (btn) {
          btn.disabled = !variant.available;
          var label = btn.querySelector('[data-otm-submit-label]') || btn;
          label.textContent = variant.available
            ? btn.getAttribute('data-otm-label-add')
            : btn.getAttribute('data-otm-label-sold-out');
        });
        if (history.replaceState && variant.id) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
        dispatch('otm:variant-change', {
          variant: variant,
          featuredMediaId: variant.featured_media_id
        });
      }

      if (select) {
        select.addEventListener('change', function () { render(currentVariant()); });
      }

      if (form && ajaxAdd && window.fetch) {
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          var submitBtn = form.querySelector('[data-otm-submit]') || document.querySelector('[data-otm-submit]');
          if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
          fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(serialize(form))
          })
            .then(function (res) {
              if (!res.ok) return res.json().then(function (body) { throw new Error(body.description || 'Could not add to cart.'); });
              return res.json();
            })
            .then(function (item) {
              note(feedback, root.getAttribute('data-otm-added-text') || 'Added to your cart.', 'ok');
              dispatch('otm:cart-added', { item: item });
            })
            .catch(function (err) {
              note(feedback, err.message, 'error');
            })
            .finally(function () {
              if (submitBtn) submitBtn.removeAttribute('aria-busy');
            });
        });
      }

      render(currentVariant());
    }

    function serialize(form) {
      var fd = new FormData(form);
      var payload = { items: [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1), properties: {} }] };
      fd.forEach(function (value, key) {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match && value) payload.items[0].properties[match[1]] = value;
      });
      if (!Object.keys(payload.items[0].properties).length) delete payload.items[0].properties;
      return payload;
    }

    function note(el, message, tone) {
      if (!el) return;
      el.textContent = message;
      el.dataset.tone = tone === 'error' ? 'error' : 'ok';
    }

    return { init: init };
  })();

  /* ------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------ */
  function initQuantity(el) {
    var input = el.querySelector('input');
    el.addEventListener('click', function (event) {
      var btn = event.target.closest('button');
      if (!btn || !input) return;
      var step = btn.hasAttribute('data-otm-qty-up') ? 1 : -1;
      var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    if (input) {
      input.addEventListener('change', function () {
        var clamped = Math.max(1, parseInt(input.value, 10) || 1);
        if (String(clamped) !== input.value) input.value = clamped;
      });
    }
  }

  /* ------------------------------------------------------------------
     Quick add (pairings, related products) — one-click add to cart
     ------------------------------------------------------------------ */
  function initQuickAdd(btn) {
    btn.addEventListener('click', function () {
      var id = Number(btn.getAttribute('data-otm-quick-add'));
      if (!id || !window.fetch) return;
      var original = btn.textContent;
      btn.disabled = true;
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(function (item) {
          btn.textContent = btn.getAttribute('data-otm-added-text') || 'Added';
          dispatch('otm:cart-added', { item: item });
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 2000);
        })
        .catch(function () {
          btn.textContent = btn.getAttribute('data-otm-error-text') || 'Try again';
          btn.disabled = false;
        });
    });
  }

  /* ------------------------------------------------------------------
     Sticky Add to Cart (mobile) — appears when the buy box scrolls away
     ------------------------------------------------------------------ */
  function initStickyAtc(el) {
    var targetSelector = el.getAttribute('data-otm-observe') || '[data-otm-buy-box]';
    var target = document.querySelector(targetSelector);
    var formId = el.getAttribute('data-otm-form-id');

    if (target && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            el.removeAttribute('data-visible');
          } else if (entry.boundingClientRect.top < 0) {
            el.setAttribute('data-visible', '');
          }
        });
      }, { threshold: 0 });
      observer.observe(target);
    }

    document.addEventListener('otm:variant-change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      var price = el.querySelector('[data-otm-sticky-price]');
      var btn = el.querySelector('[data-otm-sticky-submit]');
      if (price) price.textContent = variant.price_formatted;
      if (btn) btn.disabled = !variant.available;
    });

    var submit = el.querySelector('[data-otm-sticky-submit]');
    if (submit) {
      submit.addEventListener('click', function () {
        var form = formId && document.getElementById(formId);
        if (form) {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
      });
    }
  }

  /* ------------------------------------------------------------------
     Recently viewed — localStorage, rendered client-side
     ------------------------------------------------------------------ */
  var OtmRecent = (function () {
    function read() {
      try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
    }

    function register(el) {
      var data;
      try { data = JSON.parse(el.textContent); } catch (e) { return; }
      if (!data || !data.handle) return;
      var list = read().filter(function (p) { return p.handle !== data.handle; });
      list.unshift(data);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (e) { /* storage unavailable */ }
    }

    function render(el) {
      var exclude = el.getAttribute('data-otm-exclude');
      var limit = parseInt(el.getAttribute('data-otm-limit'), 10) || 8;
      var row = el.querySelector('[data-otm-recent-row]');
      var items = read().filter(function (p) { return p.handle !== exclude; }).slice(0, limit);
      if (!row || !items.length) return;
      row.innerHTML = items.map(function (p) {
        return (
          '<div class="otm-card">' +
          '<div class="otm-card__media">' +
          (p.image ? '<img src="' + encodeURI(p.image) + '" alt="" width="480" height="480" loading="lazy">' : '') +
          '</div>' +
          '<h3 class="otm-card__title"><a class="otm-card__link" href="' + encodeURI(p.url) + '">' + escapeHtml(p.title) + '</a></h3>' +
          '<div class="otm-price">' + escapeHtml(p.price || '') + '</div>' +
          '</div>'
        );
      }).join('');
      el.hidden = false;
    }

    function escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    return { register: register, render: render };
  })();

  /* ------------------------------------------------------------------
     Recommendations — Shopify product recommendations via section render
     ------------------------------------------------------------------ */
  function initRecommendations(el) {
    var url = el.getAttribute('data-otm-recommendations-url');
    var blockId = el.getAttribute('data-otm-block-id');
    if (!url || !window.fetch) return;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = blockId
          ? doc.querySelector('[data-otm-block-id="' + blockId + '"]')
          : doc.querySelector('[data-otm-recommendations-url]');
        if (fresh && fresh.querySelector('.otm-card')) {
          el.innerHTML = fresh.innerHTML;
          el.hidden = false;
        }
      })
      .catch(function () { /* leave hidden */ });
  }

  /* ------------------------------------------------------------------
     Reviews — client-side sort + filter of rendered review cards
     ------------------------------------------------------------------ */
  function initReviews(el) {
    var grid = el.querySelector('[data-otm-reviews-grid]');
    var sortSelect = el.querySelector('[data-otm-reviews-sort]');
    var photosOnly = el.querySelector('[data-otm-reviews-photos]');
    var ratingSelect = el.querySelector('[data-otm-reviews-rating]');
    var empty = el.querySelector('[data-otm-reviews-empty]');
    if (!grid) return;

    function cards() {
      return Array.prototype.slice.call(grid.querySelectorAll('[data-otm-review]'));
    }

    function apply() {
      var list = cards();
      var sort = sortSelect ? sortSelect.value : 'newest';
      list.sort(function (a, b) {
        if (sort === 'highest') return num(b, 'rating') - num(a, 'rating');
        if (sort === 'lowest') return num(a, 'rating') - num(b, 'rating');
        return num(b, 'order') - num(a, 'order');
      });
      var minRating = ratingSelect ? Number(ratingSelect.value) : 0;
      var visible = 0;
      list.forEach(function (card) {
        var show = num(card, 'rating') >= minRating &&
          (!photosOnly || !photosOnly.checked || card.getAttribute('data-otm-has-photo') === 'true');
        card.hidden = !show;
        if (show) visible += 1;
        grid.appendChild(card);
      });
      if (empty) empty.hidden = visible > 0;
    }

    function num(card, key) {
      return Number(card.getAttribute('data-otm-' + key)) || 0;
    }

    [sortSelect, photosOnly, ratingSelect].forEach(function (control) {
      if (control) control.addEventListener('change', apply);
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot() {
    document.querySelectorAll('[data-otm-gallery]').forEach(OtmGallery.init);
    document.querySelectorAll('[data-otm-product-form]').forEach(OtmProductForm.init);
    document.querySelectorAll('[data-otm-qty]').forEach(initQuantity);
    document.querySelectorAll('[data-otm-quick-add]').forEach(initQuickAdd);
    document.querySelectorAll('[data-otm-sticky-atc]').forEach(initStickyAtc);
    document.querySelectorAll('script[data-otm-register-product]').forEach(OtmRecent.register);
    document.querySelectorAll('[data-otm-recently-viewed]').forEach(OtmRecent.render);
    document.querySelectorAll('[data-otm-recommendations-url]').forEach(initRecommendations);
    document.querySelectorAll('[data-otm-reviews]').forEach(initReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
